import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, XCircle, Loader2, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminSetupPage = () => {
  const [email, setEmail] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [status, setStatus] = useState(null); // 'success', 'error', 'loading'
  const [message, setMessage] = useState('');
  const [setupStatus, setSetupStatus] = useState({ available: null, reason: '' });

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/setup-status`);
      setSetupStatus({
        available: response.data.setup_available,
        reason: response.data.reason
      });
    } catch (err) {
      console.error('Failed to check setup status:', err);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/setup-admin`, {
        email: email.toLowerCase().trim(),
        secret_key: secretKey
      });
      setStatus('success');
      setMessage(response.data.message || `Admin access granted to ${email}`);
      checkSetupStatus(); // Refresh status
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Failed to setup admin. Check your credentials.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl dark:text-white">Admin Setup</CardTitle>
          <CardDescription className="dark:text-slate-400">
            Initial setup to grant admin access
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Setup Status Indicator */}
          {setupStatus.available === false && (
            <Alert className="mb-4 bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-amber-800 dark:text-amber-300">
                <strong>Setup Disabled</strong><br />
                {setupStatus.reason}
              </AlertDescription>
            </Alert>
          )}

          {setupStatus.available === true && (
            <Alert className="mb-4 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-300">
                {setupStatus.reason}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={setupStatus.available === false}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Secret Key
              </label>
              <Input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="Enter setup key"
                required
                disabled={setupStatus.available === false}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              />
            </div>

            {status === 'success' && (
              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-300">
                  {message}
                  <br />
                  <span className="text-sm">Please log out and log back in to see admin access.</span>
                </AlertDescription>
              </Alert>
            )}

            {status === 'error' && (
              <Alert className="bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800">
                <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <AlertDescription className="text-red-800 dark:text-red-300">
                  {message}
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={status === 'loading' || setupStatus.available === false}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                'Grant Admin Access'
              )}
            </Button>
          </form>

          <div className="mt-6 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              <strong>Security:</strong> This page is automatically disabled after the first admin is created. 
              Subsequent admins must be promoted via Manage Users.
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
              <strong>Emergency:</strong> Set <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">ADMIN_SETUP_OVERRIDE=true</code> in environment to re-enable.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSetupPage;
